import { Minus, Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { rp } from "./util";

export function MobileCheckoutBar({ cart, byId, disabled, onCheckout }) {
    const total = cart.reduce((s, l) => s + l.qty * (byId.get(l.item_id)?.price || 0), 0);
    const count = cart.reduce((s, l) => s + l.qty, 0);
    if (!count) return null;
    return (
        <div className="lg:hidden fixed inset-x-0 bottom-[62px] md:bottom-0 z-30 border-t border-mist bg-white/95 backdrop-blur-md px-4 py-2.5 flex items-center gap-3" data-testid="pos-mobile-bar">
            <button onClick={() => document.querySelector("[data-testid=pos-cart]")?.scrollIntoView({ behavior: "smooth" })} className="flex-1 text-left" data-testid="pos-mobile-view-cart">
                <p className="text-[10px] uppercase tracking-widest text-neutral-500">{count} item{count === 1 ? "" : "s"} · view cart</p>
                <p className="text-lg font-semibold text-ink">{rp(total)}</p>
            </button>
            <button onClick={onCheckout} disabled={disabled} className="h-12 rounded-md bg-sage-dark px-6 text-xs font-semibold uppercase tracking-[0.2em] text-white disabled:opacity-40" data-testid="pos-mobile-checkout">Checkout</button>
        </div>
    );
}

export default function Cart({ cart, byId, setCart, disabled, onCheckout }) {
    const lines = cart.map((l) => ({ ...l, it: byId.get(l.item_id) })).filter((l) => l.it);
    const total = lines.reduce((s, l) => s + l.qty * l.it.price, 0);
    const count = lines.reduce((s, l) => s + l.qty, 0);
    const setQty = (id, qty) => {
        const it = byId.get(id);
        if (qty > it.remaining_qty) return toast.error(`Only ${it.remaining_qty} left`);
        setCart((c) => (qty <= 0 ? c.filter((l) => l.item_id !== id) : c.map((l) => (l.item_id === id ? { ...l, qty } : l))));
    };
    return (
        <aside className="lg:sticky lg:top-24 self-start rounded-md border border-mist bg-white flex flex-col lg:max-h-[calc(100vh-8rem)]" data-testid="pos-cart">
            <div className="flex items-center justify-between border-b border-mist px-4 py-3">
                <h2 className="text-xs uppercase tracking-widest text-ink font-semibold">Cart</h2>
                <span className="text-xs text-neutral-500" data-testid="pos-cart-count">{count} item{count === 1 ? "" : "s"}</span>
            </div>
            <div className="flex-1 overflow-y-auto divide-y divide-mist min-h-[120px]">
                {!lines.length && <p className="p-4 text-sm text-neutral-400" data-testid="pos-cart-empty">Tap a product or scan a barcode.</p>}
                {lines.map(({ item_id, qty, it }) => (
                    <div key={item_id} className="px-4 py-3 space-y-2" data-testid={`pos-cart-line-${item_id}`}>
                        <div className="flex justify-between gap-2">
                            <div className="min-w-0">
                                <p className="text-sm text-ink leading-snug line-clamp-2">{it.name}</p>
                                <p className="text-[11px] text-neutral-500 truncate">{[it.variant, it.sku].filter(Boolean).join(" · ")}</p>
                            </div>
                            <button onClick={() => setQty(item_id, 0)} className="text-neutral-400 hover:text-red-700 shrink-0" aria-label="Remove" data-testid={`pos-cart-remove-${item_id}`}><Trash size={16} /></button>
                        </div>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center border border-mist rounded">
                                <button onClick={() => setQty(item_id, qty - 1)} className="h-8 w-8 flex items-center justify-center hover:bg-ivory" data-testid={`pos-cart-dec-${item_id}`}><Minus size={14} /></button>
                                <span className="w-8 text-center text-sm" data-testid={`pos-cart-qty-${item_id}`}>{qty}</span>
                                <button onClick={() => setQty(item_id, qty + 1)} className="h-8 w-8 flex items-center justify-center hover:bg-ivory" data-testid={`pos-cart-inc-${item_id}`}><Plus size={14} /></button>
                            </div>
                            <span className="text-sm font-medium text-ink">{rp(qty * it.price)}</span>
                        </div>
                    </div>
                ))}
            </div>
            <div className="border-t border-mist p-4 space-y-3">
                <div className="flex items-baseline justify-between">
                    <span className="text-xs uppercase tracking-widest text-neutral-500">Total</span>
                    <span className="text-2xl font-semibold text-ink" data-testid="pos-cart-total">{rp(total)}</span>
                </div>
                <button onClick={onCheckout} disabled={disabled || !lines.length} data-testid="pos-checkout-btn"
                    className="h-14 w-full rounded-md bg-sage-dark text-white text-sm font-semibold uppercase tracking-[0.2em] transition-opacity hover:opacity-90 disabled:opacity-40">
                    Checkout
                </button>
            </div>
        </aside>
    );
}
